import { DropdownOption } from '../../../shared/types';
import { normalizeLabel, resolveOptionLabel } from '../../../shared/utils';
import { isAugsburgByWerkValue } from './plantScope';
import type {
  ApplyErledigtAvYesGuardParams,
  ErledigtAvYesEnabledParams,
  NeedField,
  NeedRequestedParams,
  StatusCompletedParams,
} from './downstreamStatusUpdateToAvTypes';

export const ERLEDIGT_AV_NEED_SELECTION_TOOLTIP_KEY =
  'npiSteering.erledigt_av_need_selection_tooltip';
export const ERLEDIGT_AV_DOWNSTREAM_COMPLETION_TOOLTIP_KEY =
  'npiSteering.erledigt_av_downstream_completion_tooltip';

export const NEED_FIELDS = [
  'inspection_planning_need',
  'ncpneed',
  'cmmneed',
  'qsfaneed',
] as const;

const NEED_TO_STATUS_FIELD: Record<NeedField, string> = {
  inspection_planning_need: 'pp_erledigt',
  ncpneed: 'ncperl',
  cmmneed: 'cmmerl',
  qsfaneed: 'qsfaerl',
};

const INSPECTION_PLANNING_TRIGGER_LABELS = new Set(['full fai', 'delta fai']);
const DONE_STATE_LABELS = new Set(['done', 'rejected']);
const NO_LABEL = 'no';
const YES_LABEL = 'yes';

// Checks whether a need field is explicitly requested for the current row.
function isNeedRequested({
  field,
  row,
  allDropdowns,
}: NeedRequestedParams): boolean {
  const label = resolveOptionLabel(row[field], allDropdowns?.[field] ?? []);

  if (!label || label === NO_LABEL) {
    return false;
  }

  if (field === 'inspection_planning_need') {
    return INSPECTION_PLANNING_TRIGGER_LABELS.has(label);
  }

  return label === YES_LABEL;
}

// Checks whether a downstream status field is in a completed state.
function isStatusCompleted({
  statusField,
  row,
  allDropdowns,
}: StatusCompletedParams): boolean {
  const label = resolveOptionLabel(row[statusField], allDropdowns?.[statusField] ?? []);

  return DONE_STATE_LABELS.has(label);
}

function isNeedFieldFilled({
  field,
  row,
  allDropdowns,
}: NeedRequestedParams): boolean {
  return Boolean(resolveOptionLabel(row[field], allDropdowns?.[field] ?? []));
}

// Determines whether the AV Yes value is allowed for the current row state.
export function isErledigtAvYesEnabled({
  row,
  allDropdowns,
}: ErledigtAvYesEnabledParams): boolean {
  if (!row || !allDropdowns) {
    return false;
  }

  const isAugsburg = isAugsburgByWerkValue(row?.werk, allDropdowns?.werk);
  if (!isAugsburg) {
    return true;
  }

  const hasEmptyNeedField = NEED_FIELDS.some(
    (field) => !isNeedFieldFilled({ field, row, allDropdowns }),
  );
  if (hasEmptyNeedField) {
    return false;
  }

  const requestedNeedFields = NEED_FIELDS.filter((field) =>
    isNeedRequested({ field, row, allDropdowns }),
  );

  if (!requestedNeedFields.length) {
    return true;
  }

  return requestedNeedFields.every((field) =>
    isStatusCompleted({
      statusField: NEED_TO_STATUS_FIELD[field],
      row,
      allDropdowns,
    }),
  );
}

// Resolves the localized tooltip key for the current disabled state.
export function getErledigtAvDisabledTooltipKey({
  row,
  allDropdowns,
}: ErledigtAvYesEnabledParams): string {
  if (!row || !allDropdowns) {
    return ERLEDIGT_AV_NEED_SELECTION_TOOLTIP_KEY;
  }

  const isAugsburg = isAugsburgByWerkValue(row?.werk, allDropdowns?.werk);
  if (
    isAugsburg &&
    NEED_FIELDS.some((field) => !isNeedFieldFilled({ field, row, allDropdowns }))
  ) {
    return ERLEDIGT_AV_NEED_SELECTION_TOOLTIP_KEY;
  }

  return ERLEDIGT_AV_DOWNSTREAM_COMPLETION_TOOLTIP_KEY;
}

// Disables the Yes option until the gate allows finalization.
export function applyErledigtAvYesGuard({
  options,
  row,
  allDropdowns,
}: ApplyErledigtAvYesGuardParams): DropdownOption[] {
  const enabled = isErledigtAvYesEnabled({ row, allDropdowns });
  if (enabled) {
    return options;
  }

  return options.map((option) => {
    if (normalizeLabel(option.label) === YES_LABEL) {
      return {
        ...option,
        disabled: true,
      };
    }

    return option;
  });
}